import { useRef, useEffect } from 'react';
import { performanceService } from '@/services/PerformanceService';

/**
 * Hook to measure and track component performance metrics
 */
export function useViewPerformance(componentName: string) {
  const startTimeRef = useRef<number>(0);
  const operationsRef = useRef<Record<string, number>>({});
  const componentId = useRef<string>(`${componentName}-${Math.random().toString(36).substring(2, 9)}`).current;

  // Record component load time on mount
  useEffect(() => {
    try {
      const loadTime = performance.now() - startTimeRef.current;
      // Record the metric with null checks
      if (performanceService && typeof performanceService.recordMetric === 'function') {
        performanceService.recordMetric(componentName, 'load', loadTime);
      }

      // Return cleanup function that records component lifecycle duration
      return () => {
        try {
          const totalLifecycle = performance.now() - startTimeRef.current;
          // Record the lifecycle metric with null checks
          if (performanceService && typeof performanceService.recordMetric === 'function') {
            performanceService.recordMetric(componentName, 'lifecycle', totalLifecycle);
          }
        } catch (cleanupError) {
          // Only log critical errors
          
        }
      };
    } catch (error) {
      // Only log critical errors
      
      return () => {}; // Return empty cleanup to avoid further errors
    }
  }, [componentName, componentId]);

  // Initialize start time on hook instantiation
  startTimeRef.current = startTimeRef.current || performance.now();


  /**
   * Measures an operation's duration
   * @param operationName - Name of the operation to measure
   * @returns Function to call when operation is complete
   */
  const measureOperation = (operationName: string) => {
    try {
      const startTime = performance.now();
      operationsRef.current[operationName] = startTime;

      return () => {
        try {
          const endTime = performance.now();
          const duration = endTime - (operationsRef.current[operationName] || startTime);

          // Record the metric with null checks without logging
          if (performanceService && typeof performanceService.recordMetric === 'function') {
            performanceService.recordMetric(componentName, operationName, duration);
          }

          return duration;
        } catch (error) {
          // Only log critical errors without details
          
          return 0;
        }
      };
    } catch (error) {
      // Only log critical errors without details
      
      return () => 0;
    }
  };

  return {
    measureOperation,
  };
}